import { ensureCameraPermission } from '@unifyapps/carbon/utils/devicePermissions/ensureCameraPermission';
import { logMessage } from '@unifyapps/carbon/utils/error-reporting/log';
import { FOLDER_UPLOAD_TYPE } from '@unifyapps/defs/blocks/FileUpload/contentSchema';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { TFunction } from '@unifyapps/i18n/client';
import type { SnackbarProps } from '@unifyapps/ui/components/Snackbar/types';
import { ALL_FILE_TYPES } from '../../../../../const';
import { validateAndFilterMediaUploads } from './fileValidation';

/**
 * Checks if folder upload is enabled based on supported file types
 */
function isFolderUploadEnabled(supportedFileTypes?: string[]): boolean {
  return supportedFileTypes?.includes(FOLDER_UPLOAD_TYPE) ?? false;
}

/**
 * Gets file types for the accept attribute, excluding folder type
 */
function getAcceptFileTypes(supportedFileTypes?: string[]): string {
  if (!supportedFileTypes || supportedFileTypes.length === 0) {
    return ALL_FILE_TYPES;
  }

  const fileTypes = supportedFileTypes.filter((type) => type !== FOLDER_UPLOAD_TYPE);

  // If only folder type was selected, accept all files
  if (fileTypes.length === 0) {
    return ALL_FILE_TYPES;
  }

  return fileTypes.join(',');
}

export interface PickerResult {
  validFiles: File[];
  errors: string[];
  selectedFileCount: number;
}

export interface PickerConfig {
  multipleFiles?: boolean;
  supportedFileTypes?: string[];
  containerElement: HTMLElement | null;
  maxFileSize?: number;
  maxFiles?: number;
  t: TFunction;
  showSnackbar: (props: SnackbarProps) => void;
  device: DeviceVariantType;
}

/**
 * Common cleanup function for file picker operations
 * Removes touch event listener and input element from DOM
 */
function cleanupPickerInput(
  onCancel: () => void,
  containerElement: HTMLElement | null,
  input: HTMLInputElement,
): void {
  document.removeEventListener('touchstart', onCancel);
  if (containerElement?.contains(input)) {
    containerElement.removeChild(input);
  }
}

/**
 * Creates and configures a file input element for picker operations
 */
function createPickerInput(
  accept: string,
  multipleFiles: boolean,
  capture?: 'environment' | 'user',
  allowFolderUpload?: boolean,
): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.multiple = multipleFiles;
  input.style.display = 'none';
  if (capture) {
    input.capture = capture;
  }
  // Enable folder selection if folder upload is enabled
  if (allowFolderUpload) {
    input.setAttribute('webkitdirectory', '');
    input.setAttribute('directory', '');
  }
  return input;
}

type CameraGateDecision = 'open' | 'cancel' | 'denied';

/**
 * Camera-permission decision for `<input capture>` flows. Asks or looks up the permission,
 * then decides whether the originating tap can still open the camera.
 */
async function runCameraGate(): Promise<CameraGateDecision> {
  const { state, prompted } = await ensureCameraPermission();
  if (state === 'denied') return 'denied';
  // 'granted' and 'unavailable' both proceed ('unavailable' = no API / probe failed → open
  // un-gated). Opening the camera needs the tap still "active": the permission prompt can use
  // that up — Chromium usually keeps it (opens this tap), WebKit/iOS loses it and silently
  // ignores the click. So open only while activation is alive; otherwise settle as cancel and
  // the next tap (no prompt, cached grant) opens it. No userActivation API (WebKit < 16.4 /
  // old WebViews) = treat as used up.
  const isActivationAlive = !prompted || navigator.userActivation?.isActive === true;
  return isActivationAlive ? 'open' : 'cancel';
}

/**
 * Common file picker logic shared between camera and file upload
 */
function createPickerPromise(
  config: PickerConfig & {
    accept: string;
    capture?: 'environment' | 'user';
    operationType?: 'camera' | 'fileUpload';
    allowFolderUpload?: boolean;
  },
): Promise<PickerResult> {
  const {
    t,
    showSnackbar,
    multipleFiles,
    containerElement,
    maxFileSize = 10,
    maxFiles,
    device,
    accept,
    capture,
    operationType = 'fileUpload',
    supportedFileTypes,
    allowFolderUpload = false,
  } = config;

  return new Promise((resolve) => {
    try {
      let isPromiseResolved = false;
      // For folder uploads, always enable multiple since folders contain multiple files
      const shouldAllowMultiple = allowFolderUpload || multipleFiles || false;
      const input = createPickerInput(accept, shouldAllowMultiple, capture, allowFolderUpload);
      containerElement?.appendChild(input);

      const settleWithResult = (result: PickerResult) => {
        if (isPromiseResolved) return;
        isPromiseResolved = true;
        resolve(result);
        cleanupPickerInput(onCancel, containerElement, input);
      };

      // Mobile-specific workaround: On mobile devices, when the file picker dialog is opened,
      // the oncancel event is not reliably called when the dialog loses focus (on blur).
      // If a user touches the screen after opening the dialog, we need to detect this and
      // resolve the promise with empty results, then clean up the input element.
      // This prevents the promise from hanging indefinitely if the user dismisses the dialog
      // by touching outside of it without the cancel event firing.
      const onCancel = () => {
        settleWithResult({ validFiles: [], errors: [], selectedFileCount: 0 });
      };

      // Handle file selection
      input.onchange = () => {
        if (isPromiseResolved) return;

        const selectedFiles = Array.from(input.files || []);
        const selectedFileCount = selectedFiles.length;

        // For folder uploads, filter file types but skip the folder type check
        const fileTypesForValidation = allowFolderUpload
          ? supportedFileTypes?.filter((type) => type !== FOLDER_UPLOAD_TYPE)
          : supportedFileTypes;

        const validFiles = validateAndFilterMediaUploads({
          files: selectedFiles,
          getFileSize: (file) => ('size' in file ? file.size : undefined),
          maxFileSize,
          maxFiles,
          t,
          showSnackbar,
          supportedFileTypes: fileTypesForValidation,
        });

        settleWithResult({ validFiles, errors: [], selectedFileCount });
      };

      // Handle cancellation
      input.oncancel = onCancel;

      const isMobile = device === DeviceVariantType.MOBILE;
      const isTablet = device === DeviceVariantType.TABLET;
      const isMobileOrTablet = isMobile || isTablet;
      // Gate capture on mobile/tablet only (desktop opens file picker).
      const shouldGateCapture = Boolean(capture) && isMobileOrTablet;

      const triggerPicker = () => {
        if (isPromiseResolved) return;
        // Fallback that treats a screen touch as "dialog dismissed", for when the native
        // cancel event is unreliable. Phones: always (they often skip oncancel on blur).
        // Tablets: only for camera capture, where the dialog opens after the async
        // permission prompt and a silently-refused click would otherwise leave the promise
        // unsettled. Added right before the click so a touch during the prompt isn't read
        // as a cancel.
        if (isMobile || (shouldGateCapture && isTablet)) {
          document.addEventListener('touchstart', onCancel, { once: true });
        }
        input.click();
      };

      if (!shouldGateCapture) {
        triggerPicker();
        return;
      }

      void runCameraGate()
        .then((decision) => {
          if (decision === 'denied') {
            settleWithResult({
              validFiles: [],
              errors: ['Camera permission denied'],
              selectedFileCount: 0,
            });
            return;
          }
          if (decision === 'cancel') {
            // User just allowed the permission, but on iOS/WebKit showing that prompt used
            // up this tap, so the camera can't open on this tap even though permission is
            // now granted. Settle empty (same shape as a picker-dismiss) so it routes to
            // SkipActionHandlersError and neither success nor error handlers fire: permission
            // succeeded, nothing failed, no photo taken yet. The next tap already has the
            // grant (no prompt) and opens the camera.
            logMessage(
              'camera capture: first tap granted permission, camera opens on next tap (expected on iOS, not an error)',
            );
            settleWithResult({ validFiles: [], errors: [], selectedFileCount: 0 });
            return;
          }
          triggerPicker();
        })
        .catch((error: unknown) => {
          // gate never rejects — this guards a click() throw from applying the 'open'
          // decision, which would otherwise hang the picker promise as an unhandled rejection
          console.error('Error accessing camera:', error);
          settleWithResult({
            validFiles: [],
            errors: ['Failed to access camera'],
            selectedFileCount: 0,
          });
        });
    } catch (error) {
      if (operationType === 'camera') {
        console.error('Error accessing camera:', error);
      } else {
        console.error('Error opening file picker:', error);
      }
      resolve({
        validFiles: [],
        errors: [
          operationType === 'camera' ? 'Failed to access camera' : 'Failed to open file picker',
        ],
        selectedFileCount: 0,
      });
    }
  });
}

/**
 * Opens camera interface for capturing images on web
 * Uses file input with capture attribute to trigger camera on supported devices
 */
export function openCamera(config: PickerConfig): Promise<PickerResult> {
  return createPickerPromise({
    ...config,
    accept: 'image/*',
    capture: 'environment', // This triggers the camera on supported devices
    operationType: 'camera',
  });
}

/**
 * Opens file upload dialog for selecting files on web
 */
export function openFileUpload(config: PickerConfig): Promise<PickerResult> {
  const allowFolderUpload = isFolderUploadEnabled(config.supportedFileTypes);
  const acceptFileTypes = getAcceptFileTypes(config.supportedFileTypes);

  return createPickerPromise({
    ...config,
    accept: acceptFileTypes,
    operationType: 'fileUpload',
    allowFolderUpload,
  });
}
